import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMember } from "@/contexts/MemberAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Join() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", username: "", display_name: "", ref: "" });
  const [tierSlug, setTierSlug] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { register, memberApi, member } = useMember();

  useEffect(() => {
    axios.get(`${API}/tiers`).then((r) => setTiers(r.data || []));
    const t = params.get("tier");
    if (t) setTierSlug(t);
    const ref = params.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, ref: ref.toUpperCase() }));
      axios.get(`${API}/referral/${ref.toUpperCase()}`).then((r) => setReferrer(r.data)).catch(() => {});
    }
  }, [params]);

  useEffect(() => { if (member) nav("/account"); }, [member, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(form);
      // If user picked a paid tier, immediately kick off Stripe checkout
      if (tierSlug && tierSlug !== "explorer") {
        const { data } = await memberApi.post("/member/subscribe", { tier_slug: tierSlug, origin_url: window.location.origin });
        if (data.url) { window.location.href = data.url; return; }
      }
      nav("/account");
    } catch (e) {
      setErr(e.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-16">
      <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-10">
        <div className="hidden md:block">
          <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] mb-3">CREATE YOUR ACCOUNT</p>
          <h1 className="font-heading text-4xl lg:text-5xl text-white mb-4 tracking-tight">
            Step Inside <span className="text-[#D4AF37]">The Circle</span>
          </h1>
          <p className="text-white/50 text-sm leading-relaxed mb-8">
            Members unlock rewards, points, exclusive songs, and early merch drops. Cancel anytime.
          </p>

          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Choose your tier</p>
            {tiers.map((t) => (
              <label
                key={t.slug}
                data-testid={`join-tier-option-${t.slug}`}
                className={`flex justify-between items-center rounded border p-3 cursor-pointer transition-all ${
                  tierSlug === t.slug ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 hover:border-white/30"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="tier"
                    value={t.slug}
                    checked={tierSlug === t.slug}
                    onChange={() => setTierSlug(t.slug)}
                    className="accent-[#D4AF37]"
                  />
                  <span className="text-2xl" style={{ color: t.color }}>{t.badge_emoji}</span>
                  <div>
                    <div className="text-white text-sm font-medium">{t.name}</div>
                    <div className="text-white/40 text-[11px]">${(t.price || 0).toFixed(2)}/{t.interval || "month"}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-8">
          <h2 className="font-heading text-2xl text-white tracking-wider mb-2">Create Account</h2>
          <p className="text-white/40 text-xs mb-6">All fields required · Password 8+ chars</p>

          {referrer && (
            <div className="mb-4 p-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded" data-testid="referral-banner">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Invited By</p>
              <p className="text-white text-sm">{referrer.referrer_name} · <span className="text-white/40 font-mono text-xs">{referrer.referral_code}</span></p>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <input
              required autoComplete="email" data-testid="join-email"
              type="email" placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-white text-sm outline-none rounded"
            />
            <input
              required minLength={3} data-testid="join-username"
              type="text" placeholder="Username (unique)"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-white text-sm outline-none rounded"
            />
            <input
              data-testid="join-display-name"
              type="text" placeholder="Display Name (optional)"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-white text-sm outline-none rounded"
            />
            <input
              required minLength={8} autoComplete="new-password" data-testid="join-password"
              type="password" placeholder="Password (8+)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full bg-black border border-white/10 focus:border-[#D4AF37] px-4 py-3 text-white text-sm outline-none rounded"
            />

            {err && <p className="text-red-400 text-xs" data-testid="join-error">{err}</p>}

            <button
              disabled={busy}
              data-testid="join-submit"
              className="w-full py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest font-bold hover:bg-white transition-colors disabled:opacity-40"
            >
              {busy ? "Creating..." : tierSlug && tierSlug !== "explorer" ? "Create + Checkout" : "Create Account"}
            </button>

            <p className="text-center text-xs text-white/40 pt-2">
              Already have an account? <Link to="/login" className="text-[#D4AF37] hover:text-white" data-testid="join-login-link">Sign In</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
